import { useTranslations } from '@vocab/react';
import { Box, Text, TextLink } from 'braid-design-system';
import type React from 'react';
import { useEffect } from 'react';
import { shallowEqual, useSelector } from 'react-redux';

import getTrackingAdapter from 'src/app/dataAnalytics';
import {
  JOB_REFRESH_ALERT_VIEWED,
  JOB_REFRESH_ERROR_CTA_PRESSED,
} from 'src/app/dataAnalytics/schema/constants';
import {
  FEATURE_DATA_ANALYTICS,
  useFeatureToggleData,
} from 'src/app/featureToggle';
import type AppState from 'src/types/appState';

import { ROUTE_CREATE } from '../../../../../constants/routes';
import { Error } from '../../../../components/Error';
import { useLocalisedRoutes } from '../../../../hooks/useLocalisedRoutes';
import translations from '../../.vocab';

const RefreshError: React.FC = () => {
  const isPageAnalyticsEnabled = useFeatureToggleData(FEATURE_DATA_ANALYTICS);
  const jobId = useSelector(
    (state: AppState) => state.session.job?.id || state.session.sourceJobId,
    shallowEqual,
  );

  useEffect(() => {
    if (isPageAnalyticsEnabled) {
      const alertEventData = {
        eventName: JOB_REFRESH_ALERT_VIEWED,
        alertShown: {
          tone: 'critical',
          type: 'modal',
          position: 'content',
          message: 'Refresh isn’t available for this role',
          messageCode: 'refresh not availble',
        },
      };
      getTrackingAdapter.sendLinkEvent(alertEventData);
    }
  }, [isPageAnalyticsEnabled]);

  const onRedirectClick = () => {
    if (isPageAnalyticsEnabled) {
      const redirectEventData = {
        eventName: JOB_REFRESH_ERROR_CTA_PRESSED,
        actionOrigin: 'refresh not available',
        callToAction: 'copy ad',
      };
      getTrackingAdapter.sendLinkEvent(redirectEventData);
    }
  };

  const { localiseRoute } = useLocalisedRoutes();
  const { t } = useTranslations(translations);

  return (
    <Error heading={t('refresh-error-heading')}>
      <Box paddingBottom="large">
        <Text tone="secondary">
          <TextLink
            href={localiseRoute(
              `${ROUTE_CREATE}?referrer=copyJob&jobId=${jobId}`,
            )}
            onClick={onRedirectClick}
          >
            {t('refresh-error-subtext-link')}
          </TextLink>
          {` ${t('refresh-error-subtext')}`}
        </Text>
      </Box>
    </Error>
  );
};

export { RefreshError };
